import { useEffect, useState } from 'react';
import NextLink from 'next/link';
import Link from 'next/link';
import { getTools } from '@/api/qa';
import { ITrainersTool } from '@/types/trainers';
import { ExternalUrls } from '@/utils/url';
import { formatNumber } from '@/utils/explorer';
import { getAccessibleAndNonAccessibleTools, sortedTools } from '@/utils/sort';
import { useUser } from '@/hooks/useUser';
import { TightLayoutNoNav } from '@/components/layout/Layout';
import StarRatingIcon from '@/components/ui/icons/trainers/StarRatingIcon';
import { colors } from '@/utils/styles';
import {
  getFilterByTrainerType,
  getTrainerIcon,
  getTrainerIconFromTool,
  getTrainerLocation,
  getTrainerName,
  getTrainersPage,
  TrainerType,
} from './[id]';

let initialLoad = false;

type IconType = {
  trainer: TrainerType;
  component: (
    props: React.ComponentProps<'svg'>,
  ) => JSX.Element | React.ReactNode;
};

const getUniqueIcons = (icons: IconType[]) => {
  const componentNames = new Set();
  const uniqueIcons = icons.filter((icon: IconType) => {
    if (componentNames.has(icon.component.name)) return false;
    componentNames.add(icon.component.name);
    return true;
  });

  return uniqueIcons;
};

const icons: IconType[] = Object.values(TrainerType).map(value => {
  return {
    trainer: value,
    component: getTrainerIcon(value),
  };
});

const uniqueIcons = getUniqueIcons(icons);

const Trainers: React.FC = () => {
  const [activeFilters, setActiveFilters] = useState<string[]>(
    uniqueIcons.map(icon => icon.trainer),
  );
  const [tools, setTools] = useState<ITrainersTool[]>([]);
  const [toolsToCachedRECounts, setToolsToCachedRECounts] = useState<
    Record<string, number>
  >({});
  const [toolRenderOrder, setToolRenderOrder] = useState<
    Record<string, number>
  >({});
  const [doesUserHaveAccessToTool, setDoesUserHaveAccessToTool] = useState<
    Record<string, boolean>
  >({});
  const { user } = useUser();

  const filteredTools = tools.filter(tool => {
    return activeFilters.some(activeFilter =>
      getFilterByTrainerType(activeFilter, tool.widget),
    );
  });

  const handleActiveFilters = (name: string, isActive: boolean) => {
    if (isActive) {
      if (activeFilters.length === uniqueIcons.length) {
        setActiveFilters([name]);
      } else {
        setActiveFilters(prevState =>
          prevState.filter(filter => filter !== name),
        );
      }
    } else {
      setActiveFilters(prevState => [...prevState, name]);
    }
  };

  useEffect(() => {
    const tryToGetTools = async () => {
      const { tools, toolsToCachedRECounts, toolRenderOrder, accessMap } =
        await getTools();
      const { accessibleTools, nonAccessibleTools } =
        getAccessibleAndNonAccessibleTools(accessMap, tools);
      const allTools = sortedTools(
        accessibleTools,
        toolRenderOrder,
        toolsToCachedRECounts,
      ).concat(
        sortedTools(nonAccessibleTools, toolRenderOrder, toolsToCachedRECounts),
      );
      setTools(allTools);
      setToolRenderOrder(toolRenderOrder);
      setToolsToCachedRECounts(toolsToCachedRECounts);
      setDoesUserHaveAccessToTool(accessMap);
      initialLoad = true;
    };
    tryToGetTools();

    return () => {
      initialLoad = false;
    };
  }, []);

  if (!initialLoad) return null;

  const doesUserHaveAccess = (tool: ITrainersTool) => {
    return doesUserHaveAccessToTool[tool._id] ?? false;
  };

  const stars = [];
  const TOTAL_STARS = 4;

  if (user?.reviewerTier) {
    for (let i = 1; i <= TOTAL_STARS; i++) {
      stars.push(
        <StarRatingIcon
          key={i.toString()}
          className="size-4"
          style={{
            fill:
              i > TOTAL_STARS - user?.reviewerTier
                ? colors.neutral[0]
                : colors.neutral[700],
          }}
        />,
      );
    }
  }

  return (
    <TightLayoutNoNav>
      <div className="flex size-full flex-col">
        <div className="top-[56px] z-20 flex min-h-[auto] w-full flex-col items-center justify-center border-b border-neutral-700 bg-neutral-800 px-2 py-4 md:top-[48px] md:flex-row md:justify-between md:px-4">
          <div className="flex flex-col items-baseline justify-start lg:flex-row">
            <h1 className="text-xl font-bold md:text-2xl">Map AI Trainers</h1>
          </div>
          <div className="relative left-1/2 my-1 flex w-full -translate-x-1/2 flex-row items-center justify-center md:absolute md:my-0">
            {uniqueIcons.map(icon => {
              const isActive = activeFilters.includes(icon.trainer);
              return (
                <div
                  key={icon.component.name}
                  data-active={isActive}
                  className="m-1 size-10 cursor-pointer rounded bg-neutral-700 opacity-20 data-[active=true]:opacity-100"
                  onClick={() => handleActiveFilters(icon.trainer, isActive)}>
                  <icon.component className="size-full fill-current" />
                </div>
              );
            })}
          </div>
          {user?.reviewerTier && (
            <div className="flex items-center">{stars}</div>
          )}
        </div>
        <div className="size-full overflow-scroll bg-neutral-900">
          {tools.length === 0 ? (
            <h4 className="mb-1 mt-6 text-center font-normal">
              Map AI Trainers will be back soon.
            </h4>
          ) : (
            <div className="flex flex-col items-center justify-between px-2 py-4 text-center text-[13px] md:flex-row md:px-4 md:text-left">
              <div className="text-neutral-300">
                Numbers are updated every 10 minutes.
              </div>
              <div>
                <span className="whitespace-nowrap">
                  New to Map AI Trainers?
                </span>
                <Link
                  className="ml-1 cursor-pointer text-azure-400 no-underline hover:text-azure-500"
                  href={ExternalUrls.AITrainerDocs}
                  target="_blank">
                  <span className="whitespace-nowrap">Learn more in Docs.</span>
                </Link>
              </div>
            </div>
          )}
          <div className="grid w-full grid-cols-1 gap-3 px-2 md:grid-cols-2 xl:grid-cols-3">
            {filteredTools.map(tool => {
              const page = getTrainersPage(tool);
              const name = getTrainerName(tool);
              const location = getTrainerLocation(tool);
              // Temporarily hide counts due to cohorts. See https://hivemapper.slack.com/archives/C05H95TN31V/p1727130542835599?thread_ts=1727118750.290109&cid=C05H95TN31V
              // const formattedCachedRECount = formatNumber(
              //   toolsToCachedRECounts[tool._id] ?? 0,
              //   'short',
              //   true,
              //   0,
              // );
              const formattedCachedRECount = '';
              const isToolHighPriority = toolRenderOrder[tool._id]
                ? toolRenderOrder[tool._id] !== 0
                : false;
              const Icon = getTrainerIconFromTool(tool);
              return (
                <NextLink href={page} key={tool._id}>
                  <div
                    data-priority={isToolHighPriority}
                    data-acl={tool.acl}
                    data-access={doesUserHaveAccess(tool)}
                    className="group/trainer relative mb-2 flex h-[36px] cursor-not-allowed items-center overflow-hidden rounded bg-transparent fill-neutral-0 text-left text-neutral-0 opacity-30 transition-all duration-300 ease-in-out hover:border-neutral-600 data-[access=true]:cursor-pointer data-[acl=true]:fill-harvestGoldWarning-400 data-[acl=true]:text-harvestGoldWarning-400 data-[access=true]:opacity-100 md:mb-3 md:h-[56px]">
                    <div className="relative ml-0 mr-3 size-[42px] shrink-0 md:ml-2">
                      <Icon className="absolute left-0 top-0 z-10 size-full rounded bg-neutral-700 fill-current group-data-[priority=true]/trainer:bg-azure-700" />
                    </div>
                    <div className="z-10 my-0 mr-1 text-sm font-bold leading-tight md:text-lg md:leading-tight">
                      {name}
                    </div>
                    <div className="z-10 my-0 mr-1 text-sm md:text-lg">
                      {location}
                    </div>
                    <div className="z-10 ml-2">{tool?.rewardPoints}</div>
                    <div className="z-10 ml-auto mr-2">
                      {formattedCachedRECount}
                    </div>
                    <div className="absolute inset-0 z-0 bg-neutral-800" />
                  </div>
                </NextLink>
              );
            })}
          </div>
        </div>
      </div>
    </TightLayoutNoNav>
  );
};

export default Trainers;
